import { castPeriod, toGQLDate, toGQLPeriod } from "@ct-react/core";
import { ArticleType } from "@shared/url";
import { AgeCategory, SaleAccommodationArticleFilterInput } from "@shared/graphql";
import { AnyArticleSearch, CustomRentalAccommodationFilterInput } from "../../contexts/search";

export const parse = (params: URLSearchParams, type: ArticleType | "sold"): AnyArticleSearch => {

  const allKeys = Array.from(params.keys());
  const regions = params.getAll("o_reg");
  const objectTypes = params.getAll("o_type");
  const zips = params.getAll("o_zip");

  const common = {
    ...(params.has("nov")) && { novelty: Boolean(+params.get("nov")!) },
    ...(params.has("promo")) && { promoted: Boolean(+params.get("promo")!) },
    ...(params.has("reco")) && { recommended: Boolean(+params.get("reco")!) },
    ...(params.has("name")) && { name: params.get("name") },
    ...(regions.length > 0) && { regions: regions.map(val => +val) },
    ...(objectTypes.length > 0) && { objectTypes: objectTypes.map(val => +val) },
    ...(params.has("o_city")) && { city: params.get("o_city") },
    ...(zips.length > 0) && { zips },
    ...(params.has("o_room")) && { rooms: +params.get("o_room")! },
    ...(params.has("o_room_rg") && /\d+-\d+/.test(params.get("o_room_rg")!)) && {
      roomsRange: params.get("o_room_rg")?.split("-").map(val => +val)
    },
    ...(params.has("o_bedroom")) && { bedrooms: +params.get("o_bedroom")! },
    ...(params.has("o_bathroom")) && { bathrooms: +params.get("o_bathroom")! },
    ...(params.has("o_ls")) && { surface: +params.get("o_ls")! },
    ...(params.has("o_ls_rg") && /\d+-\d+/.test(params.get("o_ls_rg")!)) && {
      surfaceRange: params.get("o_ls_rg")?.split("-").map(val => +val)
    },
    ...(params.has("o_ps")) && { plotSurface: +params.get("o_ps")! },
    ...(params.has("o_ps_rg") && /\d+-\d+/.test(params.get("o_ps_rg")!)) && {
      plotSurfaceRange: params.get("o_ps_rg")?.split("-").map(val => +val)
    },
    ...(params.has("f_dish")) && { dishwasher: Boolean(+params.get("f_dish")!) },
    ...(params.has("f_wash")) && { washingMachine: Boolean(+params.get("f_wash")!) },
    ...(params.has("f_fire")) && { fireplace: Boolean(+params.get("f_fire")!) },
    ...(params.has("f_pool")) && { parking: Boolean(+params.get("f_pool")!) },
    ...(params.has("f_sauna")) && { sauna: Boolean(+params.get("f_sauna")!) },
    ...(params.has("f_lift")) && { lift: Boolean(+params.get("f_lift")!) },
    ...(params.has("f_park")) && { parking: Boolean(+params.get("f_park")!) },
    ...(params.has("f_bal")) && { balcony: Boolean(+params.get("f_bal")!) },
    ...(params.has("f_tce")) && { terrace: Boolean(+params.get("f_tce")!) },
    ...(params.has("f_wifi")) && { wifi: Boolean(+params.get("f_wifi")!) },
    ...(params.has("f_tv")) && { tv: Boolean(+params.get("f_tv")!) },
    ...(params.has("c_center")) && { cityCenter: Boolean(+params.get("c_center")!) },
    ...(params.has("n_shop")) && { shoppingArea: Boolean(+params.get("n_shop")!) },
    ...(params.has("n_ski")) && { skiArea: Boolean(+params.get("n_ski")!) },
    ...(params.has("n_bus")) && { busStop: Boolean(+params.get("n_bus")!) },
    ...(params.has("f_view")) && { clearView: Boolean(+params.get("f_view")!) },
    ...(params.has("o_vv")) && { virtualVisit: Boolean(+params.get("o_vv")!) }
  };

  switch (type) {

    case ArticleType.RENTAL:
      const withPeriod = params.get("b_type");
      const periodRangeFrom = params.get("b_in");
      const periodRangeTo = params.get("b_out");
      const periodMargin = params.get("b_mar");
      const flexDuration = params.get("b_dur");
      const flexMonths = params.getAll("b_mth");
      return {
        kind: ArticleType.RENTAL,
        ...common,
        ...(allKeys.some(pk => /^o_guest_[a-z]$/.test(pk))) && {
          guests: {
            ...(params.has("o_guest_a")) && { [AgeCategory.ADULT]: +params.get("o_guest_a")! },
            ...(params.has("o_guest_b")) && { [AgeCategory.BABY]: +params.get("o_guest_b")! },
            ...(params.has("o_guest_c")) && { [AgeCategory.CHILD]: +params.get("o_guest_c")! },
            ...(params.has("o_guest_e")) && { [AgeCategory.EXEMPT]: +params.get("o_guest_e")! },
            ...(params.has("o_guest_s")) && { [AgeCategory.SENIOR]: +params.get("o_guest_s")! },
          }
        },
        ...(params.has("o_guest_rg") && /\d+-\d+/.test(params.get("o_guest_rg")!)) && {
          guestsRange: params.get("o_guest_rg")?.split("-").map(val => +val)
        },
        ...(params.has("f_wave")) && { microwave: Boolean(+params.get("f_wave")!) },
        ...(params.has("f_oven")) && { oven: Boolean(+params.get("f_oven")!) },
        ...(params.has("f_ham")) && { hammam: Boolean(+params.get("f_ham")!) },
        ...(params.has("f_ht")) && { hotTub: Boolean(+params.get("f_ht")!) },
        ...(params.has("f_smk")) && { smoker: Boolean(+params.get("f_smk")!) },
        ...(params.has("f_pet")) && { pet: Boolean(+params.get("f_pet")!) },
        ...(params.has("f_dp")) && { disabledPerson: Boolean(+params.get("f_dp")!) },
        ...(!!withPeriod) && {
          ...(withPeriod === "range" && !!periodRangeFrom && !!periodRangeTo) && {
            period: {
              period: toGQLPeriod([ periodRangeFrom, periodRangeTo ]),
              ...(!!periodMargin) && { dayMargin: +periodMargin }
            }
          },
          ...(withPeriod === "flex" && !!flexDuration && !!flexMonths) && {
            flexPeriod: {
              duration: flexDuration.toUpperCase(),
              intoMonths: flexMonths.map(month => toGQLDate(month, "YYYY-MM"))
            }
          }
        },
        ...(params.has("pr_rg") && /\d+-\d+/.test(params.get("pr_rg")!)) && {
          lowestPriceRange: params.get("pr_rg")?.split("-").map(val => +val)
        }
      } as CustomRentalAccommodationFilterInput;

    case ArticleType.SALE:
    case "sold":
      return {
        kind: ArticleType.SALE,
        ...common,
        ...(params.has("o_sold")) && { sold: Boolean(+params.get("o_sold")!) },
        ...(params.has("o_new")) && { new: Boolean(+params.get("o_new")!) },
        ...(params.has("f_fur")) && { furnished: Boolean(+params.get("f_fur")!) },
        ...(params.has("o_sh")) && { secondaryHome: Boolean(+params.get("o_sh")!) },
        ...(params.has("o_ff")) && { forForeigners: Boolean(+params.get("o_ff")!) },
        ...(params.has("pr_rg") && /\d+-\d+/.test(params.get("pr_rg")!)) && {
          priceRange: params.get("pr_rg")?.split("-").map(val => +val)
        }
      } as SaleAccommodationArticleFilterInput;

  }

}

const single = (params: URLSearchParams, key: string, value?: string | number | boolean | string[] | number[],
                range?: "range") => {
  params.has(key) && params.delete(key);
  if (typeof value === "undefined") return;
  if (Array.isArray(value) && value.length === 2 && !!range)
    params.set(key, `${value[0]}-${value[1]}`);
  else if (Array.isArray(value))
    value.forEach(val => params.append(key, val.toString()));
  else if (typeof value === "boolean")
    params.set(key, (+value).toString())
  else
    params.set(key, value.toString())
}

export const rebuild = (value: AnyArticleSearch, params: URLSearchParams = new URLSearchParams()): URLSearchParams => {

  single(params, "nov", value.novelty);
  single(params, "promo", value.promoted);
  single(params, "reco", value.recommended);
  single(params, "name", value.name?.toLowerCase());
  single(params, "o_reg", value.regions);
  single(params, "o_type", value.objectTypes);
  single(params, "o_city", value.city?.toLowerCase());
  single(params, "o_zip", value.zips);
  single(params, "o_room", value.rooms);
  single(params, "o_room_rg", value.roomsRange, "range");
  single(params, "o_bedroom", value.bedrooms);
  single(params, "o_bathroom", value.bathrooms);
  single(params, "o_ls", value.surface);
  single(params, "o_ls_rg", value.surfaceRange, "range");
  single(params, "o_ps", value.plotSurface);
  single(params, "o_ps_rg", value.plotSurfaceRange, "range");
  single(params, "f_dish", value.dishwasher);
  single(params, "f_wash", value.washingMachine);
  single(params, "f_fire",value.fireplace);
  single(params, "f_pool",value.pool);
  single(params, "f_sauna", value.sauna);
  single(params, "f_lift",value.lift);
  single(params, "f_park",value.parking);
  single(params, "f_bal", value.balcony);
  single(params, "f_tce", value.terrace);
  single(params, "f_wifi", value.wifi);
  single(params, "f_tv", value.tv);
  single(params, "c_center", value.cityCenter);
  single(params, "n_shop", value.shoppingArea);
  single(params, "n_bus", value.busStop);
  single(params, "n_ski", value.skiArea);
  single(params, "f_view", value.clearView);
  single(params, "o_vv", value.virtualVisit);

  switch (value.kind) {

    case ArticleType.RENTAL:
      const rental = value as CustomRentalAccommodationFilterInput;
      if (!!rental.guests && Object.keys(rental.guests).length > 0) {
        Object.entries(rental.guests).forEach(([ ageCategory, number ]) => {
          if (number < 1) return;
          const key = `o_guest_${ageCategory.charAt(0).toLowerCase()}`;
          single(params, key, number);
        });
      }
      single(params, "o_guest_rg", rental.guestsRange, "range");
      single(params, "f_wave", rental.microwave);
      single(params, "f_oven", rental.oven);
      single(params, "f_ham", rental.hammam);
      single(params, "f_ht", rental.hotTub);
      single(params, "f_smk", rental.smoker);
      single(params, "f_pet", rental.pet);
      single(params, "f_dp", rental.disabledPerson);
      Array.from(params.keys()).filter(pk => pk.match(/^b_/)).forEach(pk => params.delete(pk));
      if (!!rental.period) {
        const period = castPeriod(rental.period.period);
        params.set("b_type", "range");
        params.set("b_in", toGQLDate(period.start));
        params.set("b_out", toGQLDate(period.end));
        !!rental.period.dayMargin && params.set("b_mar", rental.period.dayMargin.toString());
      } else if (!!rental.flexPeriod) {
        params.set("b_type", "flex");
        params.set("b_dur", rental.flexPeriod.duration.toLowerCase());
        rental.flexPeriod.intoMonths.forEach(month => params.append("b_mth", toGQLDate(month, "YYYY-MM")));
      }
      single(params, "pr_rg", rental.lowestPriceRange, "range");
      break;

    case ArticleType.SALE:
      const sale = value as SaleAccommodationArticleFilterInput;
      single(params, "o_sold", sale.sold);
      single(params, "o_new", sale.new);
      single(params, "f_fur", sale.furnished);
      single(params, "o_sh", sale.secondaryHome);
      single(params, "o_ff", sale.forForeigners);
      single(params, "pr_rg", sale.priceRange, "range");
      break;

  }

  return params;

}
